import * as AuthActions from '@/app/+state/auth/auth.actions';
import { TokenStorageService } from '@/app/core/services';
import { AuthService } from '@/app/core/services';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONFIG } from '@environments/environment';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { SESSION_SETTLE_MS, UserActivityService } from '../services/user-activity.service';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private refreshRequest: Observable<string> | null = null;
    private stateInitialized = false;

    constructor(
        private tokenStorage: TokenStorageService,
        private authService: AuthService,
        private store: Store,
        private activity: UserActivityService,
    ) {
        this.initializeStoreState();
    }

    private initializeStoreState(): void {
        const accessToken = this.tokenStorage.getAccessToken();
        const refreshToken = this.tokenStorage.getRefreshToken();
        if (accessToken && refreshToken && !this.stateInitialized) {
            this.store.dispatch(
                AuthActions.refreshTokenSuccess({
                    accessToken,
                    refreshToken,
                }),
            );
            this.stateInitialized = true;
        }
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.initializeStoreState();
        const accessToken = this.tokenStorage.getAccessToken();
        if (accessToken) {
            request = this.addTokenHeader(request, accessToken);
        }
        return next.handle(request).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    if (this.isAuthRelatedRequest(request)) {
                        return throwError(() => error);
                    }
                    return this.handle401Error(request, next);
                }
                return throwError(() => error);
            }),
        );
    }

    private isAuthRelatedRequest(request: HttpRequest<any>): boolean {
        const url = request.url;
        const apiUrl = APP_CONFIG.apiUrl;
        const authBaseUrl = `${apiUrl}/auth`;
        if (!url.startsWith(authBaseUrl)) {
            return false;
        }
        const pathAfterAuth = url.slice(authBaseUrl.length);
        const excludePatterns = [/^\/login/, /^\/logout/, /^\/challenge/, /^\/password/, /^\/refresh(?:[/?]|$)/];
        const isExcluded = excludePatterns.some((pattern) => pattern.test(pathAfterAuth));
        return isExcluded;
    }

    private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    private forceLogoutOnExpiredSession(): void {
        this.tokenStorage.clearTokens();
        // The existing logout-success effect routes to login without a page reload.
        this.store.dispatch(AuthActions.logoutSuccess());
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Another request may already have rotated the token since this one was sent.
        const currentToken = this.tokenStorage.getAccessToken();
        if (currentToken && request.headers.get('Authorization') !== `Bearer ${currentToken}`) {
            return next.handle(this.addTokenHeader(request, currentToken));
        }
        const refreshToken = this.tokenStorage.getRefreshToken();
        if (!refreshToken) {
            this.forceLogoutOnExpiredSession();
            return throwError(() => new Error('No refresh token available — session expired.'));
        }
        // Background polling must not keep a session alive past the inactivity limit.
        if (this.activity.isIdle()) {
            this.forceLogoutOnExpiredSession();
            return throwError(() => new Error('Session ended after inactivity.'));
        }
        if (!this.refreshRequest) {
            this.refreshRequest = this.authService.refreshToken(refreshToken).pipe(
                map(({ data }) => {
                    if (!data.accessToken || !data.refreshToken) throw new Error('Invalid token response');
                    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
                }),
                tap(({ accessToken, refreshToken }) => {
                    this.tokenStorage.saveTokens(accessToken, refreshToken);
                    this.store.dispatch(AuthActions.refreshTokenSuccess({ accessToken, refreshToken }));
                }),
                map(({ accessToken }) => accessToken),
                catchError((error) => {
                    // Network outages and server errors do not establish that a session expired.
                    // A rejection only ends the session if no concurrent renewal (this or
                    // another tab) has replaced the token by the time things settle.
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                        setTimeout(() => {
                            if (this.tokenStorage.getRefreshToken() === refreshToken) this.forceLogoutOnExpiredSession();
                        }, SESSION_SETTLE_MS);
                    }
                    return throwError(() => error);
                }),
                finalize(() => { this.refreshRequest = null; }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        // Errors from the retried business request must not be treated as refresh failures.
        return this.refreshRequest.pipe(
            switchMap((token) => next.handle(this.addTokenHeader(request, token))),
        );
    }
}
